import React, { useState } from "react";
import { Button, message, Modal, Form, Input, Spin } from "antd";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import PDFService from "../utils/pdfService";

const LoanAgreementGenerator = ({
  loan,
  borrower,
  nextOfKin = null,
  buttonProps = {},
  showModal = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const handleGeneratePDF = async (customData = null) => {
    setLoading(true);
    try {
      // Use custom data from modal or default loan/borrower data
      let loanData, borrowerData, nextOfKinData;

      if (customData) {
        ({ loanData, borrowerData, nextOfKinData } = customData);
      } else {
        ({ loanData, borrowerData, nextOfKinData } =
          PDFService.formatLoanDataForPDF(loan, borrower, nextOfKin));
      }

      const fullName = [borrower?.first_name, borrower?.last_name]
        .filter(Boolean)
        .join("_");
      const safeName = (fullName || borrower?.borrower_name || "borrower")
        .toString()
        .replace(/\W+/g, "_")
        .replace(/^_+|_+$/g, "");
      const filename = `loan_agreement_${safeName}_${Date.now()}.pdf`;

      // Try using HTML template first, fallback to PDF if it fails
      try {
        await PDFService.downloadPopulatedHTMLPDF(
          loanData,
          borrowerData,
          nextOfKinData,
          filename
        );
      } catch (htmlError) {
        console.warn("HTML template failed, falling back to PDF:", htmlError);
        await PDFService.downloadPopulatedPDF(
          loanData,
          borrowerData,
          nextOfKinData,
          filename
        );
      }

      message.success(
        "Loan agreement PDF generated and downloaded successfully!"
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const customData = {
          loanData: {
            agreement_date:
              values.agreement_date || new Date().toLocaleDateString("en-GB"),
            repayment_day: values.repayment_day,
            loan_period: values.loan_period,
            collateral: values.collateral,
            disbursement_account: values.disbursement_account,
            principal_amount: parseFloat(values.principal_amount),
            endorsement_fee: parseFloat(values.endorsement_fee || 0),
            vehicle_tracking_fee: parseFloat(values.vehicle_tracking_fee || 0),
          },
          borrowerData: {
            names: values.borrower_names,
            nrc: values.borrower_nrc,
            occupation: values.borrower_occupation,
            address: values.borrower_address,
            email: values.email,
            phone: values.phone,
          },
          nextOfKinData: values.next_of_kin_names
            ? {
                names: values.next_of_kin_names,
                nrc: values.next_of_kin_nrc,
                occupation: values.next_of_kin_occupation,
                address: values.next_of_kin_address,
                phone: values.next_of_kin_phone,
                relationship: values.next_of_kin_relationship,
              }
            : null,
        };

        handleGeneratePDF(customData);
        setModalVisible(false);
        form.resetFields();
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const openModal = () => {
    // Pre-populate form with existing data
    const { loanData, borrowerData, nextOfKinData } =
      PDFService.formatLoanDataForPDF(loan, borrower, nextOfKin);

    form.setFieldsValue({
      agreement_date: loanData.agreement_date,
      repayment_day: loanData.repayment_day,
      loan_period: loanData.loan_period,
      collateral: loanData.collateral,
      disbursement_account: loanData.disbursement_account,
      principal_amount: loanData.principal_amount,
      endorsement_fee: loanData.endorsement_fee,
      vehicle_tracking_fee: loanData.vehicle_tracking_fee,
      borrower_names: borrowerData.names,
      borrower_nrc: borrowerData.nrc,
      borrower_occupation: borrowerData.occupation,
      borrower_address: borrowerData.address,
      borrower_email: borrowerData.email,
      borrower_phone: borrowerData.phone,
      next_of_kin_names: nextOfKinData?.names || "",
      next_of_kin_nrc: nextOfKinData?.nrc || "",
      next_of_kin_occupation: nextOfKinData?.occupation || "",
      next_of_kin_address: nextOfKinData?.address || "",
      next_of_kin_phone: nextOfKinData?.phone || "",
      next_of_kin_relationship: nextOfKinData?.relationship || "",
    });

    setModalVisible(true);
  };

  if (!loan || !borrower) {
    return null;
  }

  return (
    <>
      {showModal ? (
        <Button
          icon={<FileTextOutlined />}
          onClick={openModal}
          loading={loading}
          {...buttonProps}
        >
          Generate Loan Agreement
        </Button>
      ) : (
        <Button
          icon={<DownloadOutlined />}
          onClick={() => handleGeneratePDF()}
          loading={loading}
          {...buttonProps}
        >
          Download Agreement
        </Button>
      )}

      <Modal
        title="Generate Loan Agreement"
        open={modalVisible}
        onOk={handleModalSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        confirmLoading={loading}
        okText="Generate PDF"
      >
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            style={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            <h4>Loan Details</h4>
            <Form.Item label="Agreement Date" name="agreement_date">
              <Input placeholder="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              label="Repayment Day"
              name="repayment_day"
              rules={[{ required: true }]}
            >
              <Input placeholder="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              label="Loan Period"
              name="loan_period"
              rules={[{ required: true }]}
            >
              <Input placeholder="e.g., 30 days" />
            </Form.Item>

            <Form.Item
              label="Collateral"
              name="collateral"
              rules={[{ required: true }]}
            >
              <Input placeholder="e.g., Vehicle" />
            </Form.Item>

            <Form.Item
              label="Disbursement Account"
              name="disbursement_account"
              rules={[{ required: true }]}
            >
              <Input placeholder="Bank account or mobile money number" />
            </Form.Item>

            <Form.Item
              label="Principal Amount (ZMW)"
              name="principal_amount"
              rules={[{ required: true }]}
            >
              <Input type="number" placeholder="0.00" />
            </Form.Item>

            <Form.Item label="Endorsement Fee (ZMW)" name="endorsement_fee">
              <Input type="number" placeholder="0.00" />
            </Form.Item>

            <Form.Item
              label="Vehicle Tracking Fee (ZMW)"
              name="vehicle_tracking_fee"
            >
              <Input type="number" placeholder="0.00" />
            </Form.Item>

            <h4>Borrower Details</h4>
            <Form.Item
              label="Full Names"
              name="borrower_names"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="NRC/Passport"
              name="borrower_nrc"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Occupation"
              name="borrower_occupation"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Address"
              name="borrower_address"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Email (Optional)"
              name="borrower_email"
              rules={[{ type: "email", message: "Please enter a valid email address" }]}
            >
              <Input placeholder="Optional" />
            </Form.Item>

            <Form.Item
              label="Phone"
              name="borrower_phone"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <h4>Next of Kin (Optional)</h4>
            <Form.Item label="Full Names" name="next_of_kin_names">
              <Input />
            </Form.Item>

            <Form.Item label="NRC/Passport" name="next_of_kin_nrc">
              <Input />
            </Form.Item>

            <Form.Item label="Occupation" name="next_of_kin_occupation">
              <Input />
            </Form.Item>

            <Form.Item label="Address" name="next_of_kin_address">
              <Input />
            </Form.Item>

            <Form.Item label="Phone" name="next_of_kin_phone">
              <Input />
            </Form.Item>

            <Form.Item label="Relationship" name="next_of_kin_relationship">
              <Input placeholder="e.g., Spouse, Parent, Sibling" />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default LoanAgreementGenerator;
